// ENTREGA-QTD-BASE (pacote de 23/10, contrato (e) e (f)): formatador único da quantidade do item de venda nas telas de
// separação, entrega e app do motorista. Lê o que o banco gravou e NUNCA multiplica: com unidade de venda mostra
// "1 CX12 (12 UN)" (quantidade e rótulo da unidade + quantidade base gravada e sigla da base); sem unidade, "12 UN";
// sem sigla conhecida (serviço, item de entrega sem item de venda, dado não carregado), só o número, como antes.

export interface UnitRef {
  name?: string | null;
  sefaz_code?: string | null;
}

// Regra de sigla da casa (I.151 1b3-a): código SEFAZ ou nome, em maiúsculas
export const siglaDe = (u: UnitRef | null | undefined) =>
  ((u?.sefaz_code || u?.name || "") as string).trim().toUpperCase();

// Trecho de select do item de venda (pacote (f)): unidade, fator, base e rótulos, pelas ligações do banco.
// EXTRA é para consultas que já trazem quantity, product_unit_id e deleted_at do item de venda (sem coluna repetida).
export const SALE_ITEM_QTY_EXTRA_SELECT =
  "unit_factor, base_quantity, " +
  "product_unit:product_units!sale_items_product_unit_id_fkey(label, measurement_unit:measurement_units!product_units_measurement_unit_id_fkey(name, sefaz_code)), " +
  "qty_product:products!sale_items_product_id_fkey(base_unit:measurement_units!products_measurement_unit_id_fkey(name, sefaz_code))";
export const SALE_ITEM_QTY_SELECT = "quantity, product_unit_id, deleted_at, " + SALE_ITEM_QTY_EXTRA_SELECT;

export interface SaleItemQtyInfo {
  quantity?: number | string | null;
  product_unit_id?: string | null;
  unit_factor?: number | string | null;
  base_quantity?: number | string | null;
  deleted_at?: string | null;
  product_unit?: { label?: string | null; measurement_unit?: UnitRef | null } | null;
  qty_product?: { base_unit?: UnitRef | null } | null;
}

const numberFmt = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 4 });

export const formatQtyNumber = (value: number | string | null | undefined): string => {
  const n = Number(value ?? 0);
  return numberFmt.format(Number.isFinite(n) ? n : 0);
};

/**
 * Texto com unidade, ou null quando não há o que acrescentar (sem item de venda, serviço, sigla desconhecida, ou item de
 * venda excluído vendido em unidade — o dado gravado nele pode estar velho): nesse caso a tela mantém o texto de hoje.
 * @param quantity quantidade da própria linha (cópia da separação/entrega ou do item de venda) — usada no item sem unidade
 * @param info dados do item de venda (SALE_ITEM_QTY_SELECT); com unidade de venda, o número vem do item de venda
 */
export function formatSaleItemQtyOrNull(
  quantity: number | string | null | undefined,
  info?: SaleItemQtyInfo | null,
): string | null {
  const baseSigla = siglaDe(info?.qty_product?.base_unit);
  if (info?.product_unit_id && info.product_unit) {
    if (info.deleted_at) return null;
    const unitText = info.product_unit.label?.trim() || siglaDe(info.product_unit.measurement_unit);
    const sold = formatQtyNumber(info.quantity ?? quantity);
    if (info.base_quantity === null || info.base_quantity === undefined) {
      return unitText ? `${sold} ${unitText}` : sold;
    }
    const base = formatQtyNumber(info.base_quantity);
    return `${sold}${unitText ? ` ${unitText}` : ""} (${base}${baseSigla ? ` ${baseSigla}` : ""})`;
  }
  return baseSigla ? `${formatQtyNumber(quantity)} ${baseSigla}` : null;
}

/** Igual a formatSaleItemQtyOrNull, com o número puro quando não há unidade a mostrar */
export function formatSaleItemQty(
  quantity: number | string | null | undefined,
  info?: SaleItemQtyInfo | null,
): string {
  return formatSaleItemQtyOrNull(quantity, info) ?? formatQtyNumber(quantity);
}

/** Item vendido em unidade diferente da base (caixa etc.): o pacote trava a edição de quantidade em campo */
export const isSoldInUnit = (info?: SaleItemQtyInfo | null) => !!(info?.product_unit_id && info.product_unit);
